import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { IntakePhotos } from "@/components/IntakePhotos";
import { ColorSwatchPicker } from "@/components/ColorSwatchPicker";
import { PickListField } from "@/components/PickListField";
import { CUSTOMER_GROUPS } from "@/pages/Customers";
import { api, formatApiErrorDetail } from "@/lib/api";
import { resetSlabOptions, useSlabOptions, withCurrent } from "@/lib/slabOptions";
import { shrinkAll } from "@/lib/shrinkImage";

const EMPTY = {
  slab_color: "",
  primary_color: "",
  accent_color: "",
  material: "Granite",
  finish: "Polished",
  thickness: "3cm",
  length_in: "",
  width_in: "",
  quantity: 1,
  supplier: "",
  brand: "",
  lot_number: "",
  serial_number: "",
  barcode: "",
  location: "",
  cost_per_sqft: "",
  sale_price_per_sqft: "",
  group_prices: {},
  stone_group: "none",
  status: "In Stock",
  new_arrival: false,
  on_sale: false,
  consignment: false,
  stocked: true,
  special_order: false,
  visualiser_enabled: true,
  show_price_on_portal: true,
  notes: "",
};

const FINISHES = ["Polished", "Honed", "Leathered", "Brushed", "Matte"];

export default function SlabForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const slabOptions = useSlabOptions();
  const [form, setForm] = useState(EMPTY);
  const [listOverride, setListOverride] = useState({});
  const lists = { ...slabOptions, ...listOverride };
  const MATERIALS = withCurrent(lists.materials, form.material);
  const THICKNESSES = withCurrent(lists.thicknesses, form.thickness);

  // Material / Thickness pick-lists are shop-wide (Settings → Slab Materials & Thicknesses)
  const saveList = (key) => async (next, blockedMsg) => {
    if (!next) {
      toast.error(blockedMsg);
      return null;
    }
    try {
      const { data } = await api.put("/settings/slab-options", { ...lists, [key]: next });
      resetSlabOptions();
      setListOverride((cur) => ({ ...cur, [key]: data[key] }));
      return data[key];
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the list");
      return null;
    }
  };
  const [error, setError] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newSup, setNewSup] = useState(null);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
    api
      .get("/brands")
      .then(({ data }) => setBrands(data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/slabs/${id}`).then(({ data }) => {
      setForm({
        ...EMPTY,
        ...data,
        cost_per_sqft: data.cost_per_sqft ?? "",
        sale_price_per_sqft: data.sale_price_per_sqft ?? "",
        group_prices: data.group_prices || {},
        stone_group: data.stone_group || "none",
      });
      setLoaded(true);
    });
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      slab_color: form.slab_color,
      primary_color: form.primary_color,
      accent_color: form.accent_color,
      material: form.material,
      finish: form.finish,
      thickness: form.thickness,
      length_in: Number(form.length_in),
      width_in: Number(form.width_in),
      quantity: Number(form.quantity) || 0,
      supplier: form.supplier,
      brand: (form.brand || "").trim(),
      serial_number: form.serial_number,
      barcode: form.barcode,
      lot_number: form.lot_number,
      location: form.location,
      cost_per_sqft:
        form.cost_per_sqft === "" ? null : Number(form.cost_per_sqft),
      sale_price_per_sqft:
        form.sale_price_per_sqft === ""
          ? null
          : Number(form.sale_price_per_sqft),
      group_prices: Object.fromEntries(
        Object.entries(form.group_prices || {})
          .filter(([, v]) => v !== "" && v !== null)
          .map(([k, v]) => [k, Number(v)]),
      ),
      stone_group: form.stone_group === "none" ? "" : form.stone_group,
      status: form.status,
      new_arrival: !!form.new_arrival,
      on_sale: !!form.on_sale,
      stocked: form.stocked !== false,
      consignment: !!form.consignment,
      special_order: !!form.special_order,
      visualiser_enabled: form.visualiser_enabled !== false,
      show_price_on_portal: form.show_price_on_portal !== false,
      notes: form.notes,
    };
    try {
      const { data } = id
        ? await api.put(`/slabs/${id}`, payload)
        : await api.post("/slabs", payload);
      for (const file of await shrinkAll(pendingPhotos)) {
        const fd = new FormData();
        fd.append("file", file);
        if (file.enhanced) fd.append("enhanced", "true");
        await api.post(`/slabs/${data.id}/photos`, fd);
      }
      toast.success(
        id ? "Slab record updated" : `Slabs ${data.code} added to stock`,
      );
      navigate(`/inventory/slabs/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const field = (label, key, { hint, ...props } = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`slab-${key.replace(/_/g, "-")}-input`}
        value={form[key]}
        onChange={(e) => set(key)(e.target.value)}
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
        {...props}
      />
      {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );

  const colorField = (label, key, { placeholder, hint } = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <ColorSwatchPicker
        testId={`slab-${key.replace(/_/g, "-")}`}
        value={form[key]}
        onChange={set(key)}
        placeholder={placeholder}
      />
      {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );

  const dropdown = (label, key, options) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Select value={form[key]} onValueChange={set(key)}>
        <SelectTrigger
          data-testid={`slab-${key.replace(/_/g, "-")}-select`}
          className="h-12 border-zinc-800 bg-zinc-900 text-base"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-zinc-800 bg-zinc-900">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`slab-${key}-${o}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // the chosen supplier's brands lead the list; anything can still be typed
  const brandOptions = [
    ...brands.filter((b) => b.supplier && b.supplier === form.supplier),
    ...brands.filter((b) => !b.supplier || b.supplier !== form.supplier),
  ];
  const brandField = () => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        Brand
      </Label>
      <Input
        data-testid="slab-brand-input"
        list="slab-brand-options"
        value={form.brand || ""}
        onChange={(e) => set("brand")(e.target.value)}
        placeholder={
          brandOptions.length ? "Pick a brand or type one" : "e.g. Q Premium"
        }
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
      />
      <datalist id="slab-brand-options">
        {brandOptions.map((b) => (
          <option key={`${b.supplier}-${b.name}`} value={b.name}>
            {b.supplier ? `${b.name} — ${b.supplier}` : b.name}
          </option>
        ))}
      </datalist>
    </div>
  );

  const supplierField = () => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        Supplier
      </Label>
      {newSup !== null ? (
        <div className="flex gap-2" data-testid="slab-new-supplier-row">
          <Input
            data-testid="slab-new-supplier-name"
            autoFocus
            value={newSup}
            onChange={(e) => setNewSup(e.target.value)}
            placeholder="Supplier name"
            className="h-12 border-zinc-800 bg-zinc-900"
          />
          <Button
            type="button"
            data-testid="slab-save-new-supplier"
            onClick={async () => {
              if (!newSup.trim()) return;
              const { data } = await api.post("/suppliers", {
                name: newSup.trim(),
              });
              setSuppliers((list) =>
                list.some((s) => s.id === data.id) ? list : [...list, data],
              );
              set("supplier")(data.name);
              setNewSup(null);
              toast.success(`${data.name} added`);
            }}
            className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            Save
          </Button>
          <Button
            type="button"
            data-testid="slab-cancel-new-supplier"
            variant="outline"
            onClick={() => setNewSup(null)}
            className="h-12 border-zinc-700 bg-transparent px-3"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Select
          key={`supplier-${loaded}-${suppliers.length}`}
          value={form.supplier || "none"}
          onValueChange={(v) => set("supplier")(v === "none" ? "" : v)}
        >
          <SelectTrigger
            data-testid="slab-supplier-select"
            className="h-12 border-zinc-800 bg-zinc-900 text-base"
          >
            <SelectValue placeholder="No supplier" />
          </SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            <SelectItem value="none">No supplier</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.name}>
                {s.name}
              </SelectItem>
            ))}
            {form.supplier &&
              !suppliers.some((s) => s.name === form.supplier) && (
                <SelectItem value={form.supplier}>{form.supplier}</SelectItem>
              )}
          </SelectContent>
        </Select>
      )}
      {newSup === null && (
        <button
          type="button"
          data-testid="slab-add-supplier-inline"
          onClick={() => setNewSup("")}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-yellow-400 hover:underline"
        >
          + Add supplier
        </button>
      )}
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit slab" : "Add slabs"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Slab Record" : "Add Slabs To Stock"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="slab-form"
        >
          {field("Slab Color", "slab_color", {
            required: true,
            placeholder: "Calacatta Gold",
          })}

          <div className="grid gap-5 sm:grid-cols-2">
            {colorField("Primary Color", "primary_color", {
              placeholder: "White",
              hint: "Tap a swatch or type — separate multiple with commas",
            })}
            {colorField("Accent Color", "accent_color", {
              placeholder: "Gold, Black",
              hint: "Tap a swatch or type — separate multiple with commas",
            })}
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <PickListField
              label="Material"
              testKey="material"
              value={form.material}
              onChange={set("material")}
              options={MATERIALS}
              onSaveList={saveList("materials")}
              placeholder="e.g. Dolomite"
              normalize={(v) => v.replace(/\s+/g, " ")}
              minOne="Keep at least one material"
            />
            {dropdown("Finish", "finish", FINISHES)}
            {supplierField()}
            {brandField()}
          </div>

          <div
            className="space-y-5 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="slab-size-panel"
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Size & identification
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              <PickListField
                label="Thickness"
                testKey="thickness"
                value={form.thickness}
                onChange={set("thickness")}
                options={THICKNESSES}
                onSaveList={saveList("thicknesses")}
                placeholder="e.g. 1.2cm or 6mm"
                normalize={(v) => {
                  const s = v.replace(/\s+/g, "");
                  return /^\d+(\.\d+)?$/.test(s) ? `${s}cm` : s;
                }}
                minOne="Keep at least one thickness"
              />
              {field("Length (in)", "length_in", {
                type: "number",
                step: "0.1",
                placeholder: "126",
              })}
              {field("Width (in)", "width_in", {
                type: "number",
                step: "0.1",
                placeholder: "63",
              })}
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              {field("Serial #", "serial_number", { placeholder: "SN-4471-B" })}
              {field("Lot / Bundle", "lot_number", { placeholder: "BND-8842" })}
              {field("Barcode", "barcode", {
                placeholder: "Scan or type barcode",
              })}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {field("Quantity", "quantity", { type: "number", min: "0" })}
              {field("Rack / Location", "location", { placeholder: "A-03" })}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {field("Cost per Sq Ft ($)", "cost_per_sqft", {
              type: "number",
              step: "0.01",
              placeholder: "18.50",
            })}
            {field("Sale Price per Sq Ft ($)", "sale_price_per_sqft", {
              type: "number",
              step: "0.01",
              placeholder: "45.00",
            })}
            {dropdown("Stone Group", "stone_group", [
              "none",
              ...Array.from({ length: 16 }, (_, i) => `Group ${i}`),
            ])}
            {dropdown("Status", "status", [
              "In Stock",
              "Out of Stock",
              "Reserved",
              "Consumed",
              "Special Order",
            ])}
          </div>

          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="slab-group-prices"
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Price per sq ft by account group
            </div>
            <p className="mt-1 text-xs text-zinc-600">
              Leave blank to use the sale price with that group&apos;s discount
              from Settings → Customer portal.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {CUSTOMER_GROUPS.map((g) => (
                <div key={g} className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">
                    {g}
                  </Label>
                  <Input
                    data-testid={`slab-group-price-${g}`}
                    type="number"
                    step="0.01"
                    placeholder="—"
                    value={(form.group_prices || {})[g] ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        group_prices: {
                          ...(f.group_prices || {}),
                          [g]: e.target.value,
                        },
                      }))
                    }
                    className="h-12 border-zinc-800 bg-zinc-950 text-base"
                  />
                </div>
              ))}
            </div>
          </div>

          <label
            data-testid="slab-show-price-portal-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="slab-show-price-portal-input"
              checked={form.show_price_on_portal !== false}
              onChange={(e) => set("show_price_on_portal")(e.target.checked)}
              className="h-5 w-5 accent-emerald-500"
            />
            <span>
              <span className="block text-sm font-semibold">
                Show price on portal
              </span>
              <span className="block text-xs text-zinc-500">
                On: this slab&apos;s price can appear to customers (when portal
                pricing is enabled in Settings). Off: hide the price for this slab only
              </span>
            </span>
          </label>

          <div
            className="grid gap-3 sm:grid-cols-2"
            data-testid="slab-marketing-flags"
          >
            <label
              data-testid="slab-new-arrival-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <input
                type="checkbox"
                data-testid="slab-new-arrival-input"
                checked={!!form.new_arrival}
                onChange={(e) => set("new_arrival")(e.target.checked)}
                className="h-5 w-5 accent-emerald-500"
              />
              <span>
                <span className="block text-sm font-semibold">New arrival</span>
                <span className="block text-xs text-zinc-500">
                  Flags it in the showroom and the customer portal
                </span>
              </span>
            </label>
            <label
              data-testid="slab-on-sale-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <input
                type="checkbox"
                data-testid="slab-on-sale-input"
                checked={!!form.on_sale}
                onChange={(e) => set("on_sale")(e.target.checked)}
                className="h-5 w-5 accent-amber-500"
              />
              <span>
                <span className="block text-sm font-semibold">On sale</span>
                <span className="block text-xs text-zinc-500">
                  Shows an On sale badge to customers
                </span>
              </span>
            </label>
          </div>

          <label
            data-testid="slab-stocked-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="slab-stocked-input"
              checked={form.stocked !== false}
              onChange={(e) => set("stocked")(e.target.checked)}
              className="h-5 w-5 accent-indigo-500"
            />
            <span>
              <span className="block text-sm font-semibold">
                Track inventory
              </span>
              <span className="block text-xs text-zinc-500">
                On: counted in stock and included in low-stock alerts. Off:
                shown as supplier stock — not counted, no alerts
              </span>
            </span>
          </label>

          <label
            data-testid="slab-consignment-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="slab-consignment-input"
              checked={!!form.consignment}
              onChange={(e) => set("consignment")(e.target.checked)}
              className="h-5 w-5 accent-indigo-500"
            />
            <span>
              <span className="block text-sm font-semibold">
                Consignment slab
              </span>
              <span className="block text-xs text-zinc-500">
                Supplier-owned until it is used or sold
              </span>
            </span>
          </label>

          <label
            data-testid="slab-special-order-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="slab-special-order-input"
              checked={!!form.special_order}
              onChange={(e) => set("special_order")(e.target.checked)}
              className="h-5 w-5 accent-sky-500"
            />
            <span>
              <span className="block text-sm font-semibold">Special order</span>
              <span className="block text-xs text-zinc-500">
                Made to order — not counted in stock, but still listed on the
                public website (no badge is shown to shoppers)
              </span>
            </span>
          </label>

          <label
            data-testid="slab-visualiser-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="slab-visualiser-input"
              checked={form.visualiser_enabled !== false}
              onChange={(e) => set("visualiser_enabled")(e.target.checked)}
              className="h-5 w-5 accent-violet-500"
            />
            <span>
              <span className="block text-sm font-semibold">
                AI room visualiser on the website
              </span>
              <span className="block text-xs text-zinc-500">
                {form.visualiser_enabled !== false
                  ? "On — shoppers can render this stone into a room from its public product page (global switch: Settings → AI images)"
                  : "Off — the visualiser is hidden on this stone's public page and its AI endpoints refuse it"}
              </span>
            </span>
          </label>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="slab-notes-input"
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              rows={4}
              placeholder="Veining direction, bookmatch pair, minor fissure…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          {error && (
            <p
              data-testid="slab-form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}

          <IntakePhotos
            files={pendingPhotos}
            onChange={setPendingPhotos}
            testPrefix="slab-intake"
          />

          <div className="no-print fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-slab-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Add To Stock"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
